import React, {useEffect, useState} from 'react';
import {Button, Modal} from "react-bootstrap";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faBars,
    faChevronLeft,
    faChevronRight, faClipboardList,
    faEnvelopeOpenText,
    faFileInvoiceDollar,
    faList, faPaperPlane
} from "@fortawesome/free-solid-svg-icons";
import {CalculatedOption, Item, KoluSet} from "../../config/interfaces";
import {Brand, CableTrayType, HouseType, PlacementOutside, RoomType, WallType} from "../../config/enums";
import './TotalOverviewDialog.scss';
import StepperStep from "../Stepper/StepperStep";
import Stepper from "../Stepper/Stepper";
import SendForm from "./SendForm";
import Overview from "./Overview";
import UserForm from "./user-form.interface";
import SetsOverview from "./SetsOverview";
import {cloneDeep} from 'lodash';
import OptionsStep from "./OptionsStep";
import {validate, ValidationError} from "class-validator";
import {NotificationManager} from "react-notifications";
import axios from "axios";

interface TotalOverviewModalProps {
    close: () => void;
    show: boolean;
    calculatedOptions: CalculatedOption[];
    items: Item[];
    setCalculatedOptions: (calculatedOptions: CalculatedOption[]) => void;
    calculatedToggle: boolean;
    houseType: HouseType;
    wallType: WallType;
    constructionYear: number;
    aircoItems: Item[];
    setShowFinalModal: (showFinalModal: boolean) => void;
}

/**
 * Component that defines a dialog to finish the calculated options and choose the brand.
 * @param {TotalOverviewModalProps} props - The props that the TotalOverviewDialog component accepts.
 * @param {Function} props.close - Function to close the dialog.
 * @param {boolean} props.show - Boolean if dialog should be displayed.
 * @param {CalculatedOption[]} props.calculatedOptions - Array of calculated options, every brand that is selected has one option in this array.
 * @param {Item[]} props.items - List of possible airco items that can be chosen as perfect combination after set changes.
 * @param {Function} props.setCalculatedOptions - Function to change calculated options.
 * @param {boolean} props.calculatedToggle - Boolean that works as a toggle to track whenever a new calculation has happened.
 * @constructor
 */
const TotalOverviewDialog = (props: TotalOverviewModalProps) => {
    const {setShowFinalModal, close, show, calculatedOptions, setCalculatedOptions, items, calculatedToggle, constructionYear, houseType, wallType} = props;
    const cheapest = calculatedOptions.sort((a, b) => a.totalPriceExcl! - b.totalPriceExcl!)[0];
    const [chosenBrand, setChosenBrand] = useState<Brand>(cheapest.brand);
    const [currentStep, setCurrentStep] = useState<number>(1);
    const [form, setForm] = useState<UserForm>({
        city: "",
        country: "",
        email: "",
        firstName: "",
        lastName: "",
        phone: "",
        postalCode: "",
        streetAndNumber: ""
    });
    const [formErrors, setFormErrors] = useState<ValidationError[]>([]);

    const submit = (e: React.FormEvent | React.MouseEvent) => {
        e.preventDefault();
        const frm = new UserForm();
        frm.city = form.city;
        frm.postalCode = form.postalCode;
        frm.country = form.country;
        frm.email = form.email;
        frm.phone = form.phone;
        frm.firstName = form.firstName;
        frm.lastName = form.lastName;
        frm.streetAndNumber = form.streetAndNumber;
        validate(frm).then((errors) => {
            setFormErrors(errors);
            if (errors.length === 0) {
                const chosenOption = calculatedOptions.filter(co => co.brand === chosenBrand)[0];
                if (!chosenOption) {
                    NotificationManager.error('Er is achterliggend een fout opgetreden. Probeer het zo dadelijk nog eens.');
                    return;
                }

                const sets = cloneDeep(chosenOption.sets) as any;
                sets.map((set: any) => {
                    set.placementOutside = PlacementOutside[set.placementOutside];
                    set.rooms.map((room: any) => {
                        room.cableTrayType = CableTrayType[room.cableTrayType];
                        room.type = RoomType[room.type];
                        delete room.i;
                        return room;
                    });
                    return set;
                })

                const solution = {
                    combination: {
                        brand: chosenOption.brand,
                        sets: sets,
                        extraItems: chosenOption.extraItems,
                        settings: {
                            "houseType": HouseType[houseType],
                            "constructionYear": constructionYear,
                            "wallType": WallType[wallType]
                        }
                    },
                    form,
                    calculatedPriceIncl: chosenOption.totalPriceIncl,
                    calculatedPriceExcl: chosenOption.totalPriceExcl
                }

                axios.post('https://europe-west1-kolu-v2.cloudfunctions.net/KoluCalculatorPublish', solution).then((response) => {
                    // NotificationManager.success('Je aanvraag werd succesvol verzonden!');
                    close();
                    setShowFinalModal(true);
                }).catch((err) => {
                    NotificationManager.error('Er is een probleem opgetreden met het verzenden van je gegevens... Probeer het zo dadelijk opnieuw aub.');
                });
            } else {
                NotificationManager.error('De validatie van het formulier is mislukt.');
            }

        }).catch(() => {
            NotificationManager.error('Er zijn fouten gevonden in het formulier...');
        });
    }

    useEffect(() => {
        const cheapest = calculatedOptions.sort((a, b) => a.totalPriceExcl! - b.totalPriceExcl!)[0];
        setChosenBrand(cheapest.brand);
        setCurrentStep(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [calculatedToggle]);

    const setSets = (sets: KoluSet[]) => {
        const calculatedOptionsCopy = cloneDeep(calculatedOptions);
        calculatedOptionsCopy.filter(o => o.brand === chosenBrand)[0].sets = sets;
        setCalculatedOptions(calculatedOptionsCopy);
    }

    const sets = calculatedOptions.filter(co => co.brand === chosenBrand)[0]?.sets;
    return <Modal show={show} onHide={close} size={'lg'}>
        <Modal.Header closeButton>
            <Modal.Title><FontAwesomeIcon icon={faFileInvoiceDollar} /> Totaal</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            <Stepper currentStep={currentStep} setCurrentStep={setCurrentStep}>
                <StepperStep text={'Combinaties'} stepIcon={<FontAwesomeIcon icon={faBars} />}>
                    <SetsOverview sets={sets} setSets={setSets} calculatedOptions={calculatedOptions} items={items} setCalculatedOptions={setCalculatedOptions} />
                </StepperStep>
                <StepperStep text={'Opties'} stepIcon={<FontAwesomeIcon icon={faClipboardList} />}>
                    <OptionsStep aircoItems={props.aircoItems} sets={sets} setSets={setSets} calculatedOptions={calculatedOptions} chosenBrand={chosenBrand} setChosenBrand={setChosenBrand} />
                </StepperStep>
                <StepperStep text={'Overzicht'} stepIcon={<FontAwesomeIcon icon={faList} />}>
                    <Overview calculatedOptions={calculatedOptions} chosenBrand={chosenBrand} cheapest={cheapest} sets={sets} />
                </StepperStep>
                <StepperStep text={'Aanvragen'} stepIcon={<FontAwesomeIcon icon={faEnvelopeOpenText} />}>
                    <SendForm submit={submit} form={form} setForm={setForm} errors={formErrors} setErrors={setFormErrors} />
                </StepperStep>
            </Stepper>
        </Modal.Body>
        <Modal.Footer>
            <Button variant="secondary" onClick={close}>
                Sluiten
            </Button>
            {currentStep > 1 && <Button variant="secondary" onClick={() => setCurrentStep(currentStep - 1)}>
                <FontAwesomeIcon icon={faChevronLeft} /> Terug
            </Button>}
            {currentStep <= 3 && <Button variant="main" onClick={() => setCurrentStep(currentStep + 1)}>
                {[2, 3].includes(currentStep) ? `Kiezen voor ${chosenBrand}` : 'Verder'} <FontAwesomeIcon icon={faChevronRight} />
            </Button>}
            {currentStep === 4 && <Button variant="main" onClick={submit} id={'finish'}>
                Verzenden <FontAwesomeIcon icon={faPaperPlane} />
            </Button>}
        </Modal.Footer>
    </Modal>;
}

export default TotalOverviewDialog;